/**
 * Web presentation wrapper around zen-core-elements' ZenButton.
 *
 * The problem being fixed (spec 13.7):
 *
 *   ZenButton has no height of its own. It derives one from `paddingVertical`
 *   applied to the *title text* (10 for md, 15 for lg, 7 for sm), so the
 *   rendered height is "label line box + padding" - and the label's line box
 *   comes from font metrics, which react-native-web resolves differently from
 *   the native text engine. Two buttons with the same size prop therefore end
 *   up different heights in a browser, and a [Cancel] [Save] pair sits
 *   crooked.
 *
 *   The fix is to stop deriving height from text: pin a minHeight per size and
 *   let the existing `alignItems: center` on the button row do the centring.
 *   `md` equals webControl.height so a button that sits beside an input or a
 *   dropdown lines up with it.
 *
 * As with the input wrapper, defaults are applied before the caller's styles,
 * so a screen that already sized a button keeps its own dimensions.
 */

import React from 'react';
import {StyleSheet} from 'react-native';
import BaseZenButton, {
  ZenButtonProps,
} from 'zen-core-elements/src/elements/zenButton/zenButton';

import {webButton, webFontFamily, webLineHeight} from './webTokens';

const styles = StyleSheet.create({
  sm: {minHeight: webButton.sm.minHeight, paddingHorizontal: webButton.sm.paddingHorizontal},
  md: {minHeight: webButton.md.minHeight, paddingHorizontal: webButton.md.paddingHorizontal},
  lg: {minHeight: webButton.lg.minHeight, paddingHorizontal: webButton.lg.paddingHorizontal},

  /**
   * The width floor (16.1, 16.2), kept separate from the size preset because
   * it is applied conditionally - see `widthFloor` below.
   */
  minWidthSm: {minWidth: webButton.sm.minWidth},
  minWidthMd: {minWidth: webButton.md.minWidth},
  minWidthLg: {minWidth: webButton.lg.minWidth},

  titleSm: {
    fontFamily: webFontFamily.regular,
    fontSize: webButton.sm.fontSize,
    lineHeight: webLineHeight.of(webButton.sm.fontSize, 1.2),
    paddingVertical: 0,
  },
  titleMd: {
    fontFamily: webFontFamily.regular,
    fontSize: webButton.md.fontSize,
    lineHeight: webLineHeight.of(webButton.md.fontSize, 1.2),
    paddingVertical: 0,
  },
  titleLg: {
    fontFamily: webFontFamily.regular,
    fontSize: webButton.lg.fontSize,
    lineHeight: webLineHeight.of(webButton.lg.fontSize, 1.2),
    paddingVertical: 0,
  },

  /**
   * ZenButton gives its `icon` prop a 5px trailing margin, but the
   * `leftIcon` / `rightIcon` wrappers get nothing at all - they render with
   * whatever style the call site passes, which is usually none. The result is
   * icons sitting flush against the label on some buttons and spaced on
   * others. These supply the missing gap.
   */
  leftIcon: {marginRight: webButton.iconGap},
  rightIcon: {marginLeft: webButton.iconGap},
});

const bySize = {
  sm: {button: styles.sm, title: styles.titleSm, minWidth: styles.minWidthSm},
  md: {button: styles.md, title: styles.titleMd, minWidth: styles.minWidthMd},
  lg: {button: styles.lg, title: styles.titleLg, minWidth: styles.minWidthLg},
} as const;

/**
 * The width floor applies only to buttons that have not been sized by hand.
 *
 * Plenty of screens do set a width - 45, 60, 80, 100, 110, 150 across the app -
 * and a floor that overrode those would widen buttons that are deliberately
 * narrow. Reading the caller's own style and standing down when it already
 * decides the width keeps this a default rather than an override, which is how
 * the rest of this wrapper behaves.
 */
function widthFloor(
  buttonStyle: ZenButtonProps['buttonStyle'],
  preset: (typeof bySize)[keyof typeof bySize],
) {
  const caller = StyleSheet.flatten(buttonStyle) ?? {};
  const sized = caller.width !== undefined || caller.minWidth !== undefined;
  return sized ? null : preset.minWidth;
}

const WebZenButton = ({
  buttonStyle,
  titleStyle,
  size,
  leftIconStyle,
  rightIconStyle,
  ...props
}: ZenButtonProps) => {
  // ZenButton itself treats "no size" as md for font sizing; match that.
  const preset = bySize[size ?? 'md'];

  const merged = {
    ...props,
    size,
    leftIconStyle: [styles.leftIcon, leftIconStyle],
    rightIconStyle: [styles.rightIcon, rightIconStyle],
    /**
     * Tags the rendered element so the stylesheet can give every button
     * the same hover feedback (see globalStyles.web.ts). react-native has
     * no hover state, and adding one per call site is exactly the
     * duplication this layer exists to avoid; react-native-web turns
     * `dataSet` into data-* attributes, which is the one hook a stylesheet
     * can reach. Pressed (activeOpacity), disabled and focus are already
     * covered - by ZenButton, ZenButton and globalStyles.
     */
    dataSet: {zenButton: size ?? 'md'},
    buttonStyle: [preset.button, widthFloor(buttonStyle, preset), buttonStyle],
    /*
     * paddingVertical: 0 in the title cancels the height-from-text
     * behaviour described above. It comes *before* the caller's titleStyle
     * so a screen that deliberately pads a label still can.
     */
    titleStyle: [preset.title, titleStyle],
  };

  /*
   * One cast for the whole prop bag. ZenButtonProps is a discriminated
   * union on `type`, so spreading into it member-by-member makes TypeScript
   * re-check the discriminant it cannot see through - and `dataSet` is a
   * react-native-web extension the shared react-native types do not declare
   * at all. Nothing about the values changes.
   */
  return <BaseZenButton {...(merged as unknown as ZenButtonProps)} />;
};
export default WebZenButton;
